import { Box, ButtonBase, Stack, Typography } from '@mui/material';

export type ScoreDirection = 'rising-bad' | 'rising-good' | 'neutral';

export interface ScorePickerProps {
  label: string;
  hint: string;                                 // e.g. "0 none · 10 worst"
  value: number | null;
  onChange: (value: number | null) => void;
  direction?: ScoreDirection;
}

// Chip-style 0–10 picker — one tap per value, no slider ambiguity.
// Colour grades left-to-right so high pain/breathlessness glow red and high
// sleep/appetite/mood/appearance glow green.
export default function ScorePicker({
  label,
  hint,
  value,
  onChange,
  direction = 'neutral',
}: ScorePickerProps) {
  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {value === null ? (
            <Typography variant="caption" color="text.secondary">not set</Typography>
          ) : (
            <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {value}/10
            </Typography>
          )}
          {value !== null && (
            <ButtonBase
              onClick={() => onChange(null)}
              sx={{
                fontSize: 12,
                px: 1, py: 0.25,
                borderRadius: 1,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              clear
            </ButtonBase>
          )}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        spacing={{ xs: 0.5, sm: 0.75 }}
        sx={{ width: '100%', flexWrap: 'nowrap' }}
      >
        {Array.from({ length: 11 }, (_, n) => {
          const selected = value === n;
          return (
            <ButtonBase
              key={n}
              onClick={() => onChange(n)}
              sx={{
                flex: 1,
                minWidth: 0,
                aspectRatio: '1 / 1',
                borderRadius: 1.5,
                border: 1,
                borderColor: selected ? tintColor(n, direction, 'border') : 'divider',
                bgcolor: selected ? tintColor(n, direction, 'bg') : 'transparent',
                color: selected ? tintColor(n, direction, 'fg') : 'text.primary',
                fontWeight: selected ? 700 : 400,
                fontSize: { xs: 14, sm: 15 },
                transition: 'border-color 120ms, background-color 120ms, color 120ms, transform 80ms',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: selected ? tintColor(n, direction, 'bg') : 'action.hover',
                },
                '&:active': { transform: 'scale(0.96)' },
              }}
              aria-pressed={selected}
              aria-label={`${label} ${n}`}
            >
              {n}
            </ButtonBase>
          );
        })}
      </Stack>
    </Box>
  );
}

// Colour ramp for a 0–10 value. `rising-bad` means high = red (pain etc).
// `rising-good` means high = green (sleep etc). Neutral uses primary.
function tintColor(
  value: number,
  direction: ScoreDirection,
  slot: 'bg' | 'border' | 'fg',
): string {
  if (direction === 'neutral') {
    return slot === 'fg' ? 'primary.contrastText' : 'primary.main';
  }
  const pct = value / 10;
  // Simple hue lerp: 120 (green) → 0 (red) or reverse.
  const startHue = direction === 'rising-bad' ? 120 : 0;
  const endHue = direction === 'rising-bad' ? 0 : 120;
  const hue = startHue + (endHue - startHue) * pct;
  const saturation = 60;
  const lightness = slot === 'fg' ? 96 : slot === 'bg' ? 40 : 50;
  return `hsl(${hue} ${saturation}% ${lightness}%)`;
}
